import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Download } from 'lucide-react';
import { downloadGet, get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, money } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Button, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface PayrollRun {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  totalGrossMinor: number;
  totalNetMinor: number;
  payslipCount: number;
  processedAt: string | null;
}

interface Payslip {
  id: string;
  basicMinor: number;
  allowancesMinor: number;
  overtimeMinor: number;
  deductionsMinor: number;
  taxMinor: number;
  netMinor: number;
  employee: { id: string; name: string; employeeNo: string; designation: string | null; bankAccount: string | null };
}

interface PayrollRunDetail extends Omit<PayrollRun, 'payslipCount'> {
  payslips: Payslip[];
}

const period = (run: Pick<PayrollRun, 'periodStart' | 'periodEnd'>) => `${dateOnly(run.periodStart)} → ${dateOnly(run.periodEnd)}`;

function useRunActions() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['payroll'] });

  const process = useMutation({
    mutationFn: (id: string) => post(`/hr/payroll/${id}/process`),
    onSuccess: () => {
      toast.success('Payroll processed', 'The liability is now on the ledger');
      void refresh();
    },
    onError: (error) => toast.error('Could not process the run', describeError(error)),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => post(`/hr/payroll/${id}/paid`),
    onSuccess: () => {
      toast.success('Run marked paid');
      void refresh();
    },
    onError: (error) => toast.error('Could not mark the run paid', describeError(error)),
  });

  const bankFile = useMutation({
    mutationFn: (run: Pick<PayrollRun, 'id' | 'periodStart'>) =>
      downloadGet(`/hr/payroll/${run.id}/bank-file.csv`, `bank-transfer-${run.periodStart.slice(0, 10)}.csv`),
    onError: (error) => toast.error('Could not download the bank file', describeError(error)),
  });

  return { process, markPaid, bankFile };
}

function PayrollRunDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const actions = useRunActions();
  const detail = useQuery({
    queryKey: ['payroll', id],
    queryFn: () => get<PayrollRunDetail>(`/hr/payroll/${id}`),
  });
  const run = detail.data;
  const slips = run?.payslips ?? [];
  const sum = (pick: (slip: Payslip) => number) => slips.reduce((total, slip) => total + pick(slip), 0);

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      icon={Banknote}
      title={run ? `Payroll ${period(run)}` : 'Payroll run'}
      description={run?.status === 'draft' ? 'Draft payslips are rebuilt from attendance each time the period is re-run' : 'Frozen when the run was processed'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {run?.status === 'draft' && (
            <Button loading={actions.process.isPending} onClick={() => actions.process.mutate(run.id)}>
              Process
            </Button>
          )}
          {run?.status === 'processed' && (
            <Button loading={actions.markPaid.isPending} onClick={() => actions.markPaid.mutate(run.id)}>
              Mark paid
            </Button>
          )}
          {run && run.status !== 'draft' && (
            <Button variant="outline" loading={actions.bankFile.isPending} onClick={() => actions.bankFile.mutate(run)}>
              <Download className="h-4 w-4" /> Bank transfer file
            </Button>
          )}
        </>
      }
    >
      {run && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <StatusBadge status={run.status} />
          <span>{slips.length} payslips</span>
          {run.processedAt && <span>Processed {dateOnly(run.processedAt)}</span>}
        </div>
      )}
      <DataTable
        dense
        rows={run?.payslips}
        loading={detail.isLoading}
        error={detail.error}
        rowKey={(row) => row.id}
        empty="No payslips in this run."
        columns={[
          {
            header: 'Person',
            sort: (row) => row.employee.name,
            cell: (row) => (
              <div>
                <p>{row.employee.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{row.employee.bankAccount ?? 'No bank account on file'}</p>
              </div>
            ),
          },
          { header: 'Basic', align: 'right', sort: (row) => row.basicMinor, cell: (row) => money(row.basicMinor) },
          { header: 'Allowances', align: 'right', hideBelow: 'sm', sort: (row) => row.allowancesMinor, cell: (row) => money(row.allowancesMinor) },
          { header: 'Overtime', align: 'right', hideBelow: 'sm', sort: (row) => row.overtimeMinor, cell: (row) => money(row.overtimeMinor) },
          { header: 'Deductions', align: 'right', sort: (row) => row.deductionsMinor, cell: (row) => money(row.deductionsMinor) },
          { header: 'Tax', align: 'right', hideBelow: 'md', sort: (row) => row.taxMinor, cell: (row) => money(row.taxMinor) },
          { header: 'Net', align: 'right', sort: (row) => row.netMinor, cell: (row) => <span className="font-medium text-foreground">{money(row.netMinor)}</span> },
        ]}
        footer={
          slips.length > 0 && (
            <div className="flex w-full flex-wrap justify-end gap-x-5 gap-y-1 tabular">
              <span>Basic {money(sum((s) => s.basicMinor))}</span>
              <span>Allowances {money(sum((s) => s.allowancesMinor))}</span>
              <span>Overtime {money(sum((s) => s.overtimeMinor))}</span>
              <span>Deductions {money(sum((s) => s.deductionsMinor))}</span>
              <span>Tax {money(sum((s) => s.taxMinor))}</span>
              <span className="font-semibold text-foreground">Net {money(sum((s) => s.netMinor))}</span>
            </div>
          )
        }
      />
    </Dialog>
  );
}

export function PayrollTab() {
  const navigate = useNavigate();
  const actions = useRunActions();
  const [open, setOpen] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ['payroll'],
    queryFn: () => get<PayrollRun[]>('/hr/payroll'),
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => navigate('/people/payroll/new')}>Run payroll</Button>
      </div>

      <DataTable
        rows={runs.data}
        loading={runs.isLoading}
        refreshing={runs.isFetching}
        error={runs.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row.id)}
        empty="No payroll runs yet."
        columns={[
          { header: 'Period', sort: (row) => row.periodStart, cell: (row) => period(row) },
          { header: 'Employees', align: 'right', sort: (row) => row.payslipCount, cell: (row) => row.payslipCount },
          { header: 'Gross', align: 'right', sort: (row) => row.totalGrossMinor, cell: (row) => money(row.totalGrossMinor) },
          { header: 'Net', align: 'right', sort: (row) => row.totalNetMinor, cell: (row) => <span className="font-medium">{money(row.totalNetMinor)}</span> },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
        rowActions={(row) => (
          <>
            {row.status === 'draft' && (
              <Button size="sm" loading={actions.process.isPending && actions.process.variables === row.id} onClick={() => actions.process.mutate(row.id)}>
                Process
              </Button>
            )}
            {row.status === 'processed' && (
              <Button size="sm" loading={actions.markPaid.isPending && actions.markPaid.variables === row.id} onClick={() => actions.markPaid.mutate(row.id)}>
                Mark paid
              </Button>
            )}
            {row.status !== 'draft' && (
              <Button
                size="sm"
                variant="outline"
                aria-label="Download bank transfer file"
                loading={actions.bankFile.isPending && actions.bankFile.variables?.id === row.id}
                onClick={() => actions.bankFile.mutate(row)}
              >
                <Download className="h-4 w-4" /> Bank file
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setOpen(row.id)}>
              Open
            </Button>
          </>
        )}
      />

      {open && <PayrollRunDialog id={open} onClose={() => setOpen(null)} />}
    </>
  );
}
